import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState, TypedDispatch } from "../store";

export type WorkflowType = {
  id: number;
  title: string;
  category?: string | null;
  description: string;
  cells?: string | null;
  approved: boolean;
  views: number;
  created_at: string;
  created_by: number;
  color?: string | null;
  background?: string | null;
  icon?: string | null;
};

type WorkflowState = {
  workflows: WorkflowType[];
  loading: boolean;
  error: string | null;
};

const initialState: WorkflowState = {
  workflows: [],
  loading: false,
  error: null,
};

const workflowSlice = createSlice({
  name: "workflows",
  initialState,
  reducers: {
    setWorkflows(state, action: PayloadAction<WorkflowType[]>) {
      state.workflows = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    updateWorkflow(state, action: PayloadAction<WorkflowType>) {
      const idx = state.workflows.findIndex(wf => wf.id === action.payload.id);
      if (idx !== -1) {
        state.workflows[idx] = action.payload;
      }
    },
    deleteWorkflow(state, action: PayloadAction<number>) {
      state.workflows = state.workflows.filter(wf => wf.id !== action.payload);
    },
    addWorkflow(state, action: PayloadAction<WorkflowType>) {
      state.workflows.unshift(action.payload);
    }
  },
});

export default workflowSlice.reducer;
export const {
  setWorkflows,
  setLoading,
  setError,
  updateWorkflow,
  deleteWorkflow,
  addWorkflow,
} = workflowSlice.actions;

// Selectors
export const getUserWorkflows = (state: RootState) => state.workflows.workflows;
export const getWorkflowLoading = (state: RootState) => state.workflows.loading;
export const getWorkflowError = (state: RootState) => state.workflows.error;

// Async Actions

// 1. Fetch approved/public workflows
export const fetchPublicWorkflows = (params?: { category?: string; title?: string }) => 
  async (dispatch: TypedDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      let url = "/api/workflows/";
      if (params) {
        const searchParams = new URLSearchParams();
        if (params.category) searchParams.set("category", params.category);
        if (params.title) searchParams.set("title", params.title);
        url += "?" + searchParams.toString();
      }
      const { data } = await axios.get(url);
      dispatch(setWorkflows(data));
    } catch (error: any) {
      dispatch(setError(error?.message || "Error loading workflows"));
    } finally {
      dispatch(setLoading(false));
    }
  };

// 2. Fetch all workflows for the current user (not just approved)
export const fetchUserWorkflows = () => async (dispatch: TypedDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    // This example assumes /api/workflows/ returns all for authenticated user, adjust if needed
    const { data } = await axios.get("/api/workflows/user/");
    dispatch(setWorkflows(data));
  } catch (error: any) {
    dispatch(setError(error?.message || "Error loading workflows"));
  } finally {
    dispatch(setLoading(false));
  }
};

// 3. Edit workflow
export const editWorkflow = (id: number, updates: Partial<WorkflowType>) => async (dispatch: TypedDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const { data } = await axios.patch(`/api/workflows/${id}/`, updates);
    dispatch(updateWorkflow(data));
  } catch (error: any) {
    dispatch(setError(error?.message || "Error editing workflow"));
  } finally {
    dispatch(setLoading(false));
  }
};

// 4. Delete workflow
export const removeWorkflow = (id: number) => async (dispatch: TypedDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    await axios.delete(`/api/workflows/${id}/`);
    dispatch(deleteWorkflow(id));
  } catch (error: any) {
    dispatch(setError(error?.message || "Error deleting workflow"));
  } finally {
    dispatch(setLoading(false));
  }
};

// 5. Add (create) a workflow
export const createWorkflow = (payload: Omit<WorkflowType, "id" | "approved" | "views" | "created_at" | "created_by">) => 
  async (dispatch: TypedDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const { data } = await axios.post("/api/workflows/add/", payload);
      dispatch(addWorkflow(data));
    } catch (error: any) {
      dispatch(setError(error?.message || "Error creating workflow"));
    } finally {
      dispatch(setLoading(false));
    }
  };

// 6. Increment workflow views (returns nothing, just updates on backend)
export const incrementWorkflowViews = (id: number) => async (dispatch: TypedDispatch) => {
  try {
    await axios.post(`/api/workflows/${id}/increment-views/`, {});
    // Optionally, fetch again or manually update local views count
    // dispatch(fetchPublicWorkflows());
  } catch (error) {
    // Silent fail, or add your error handler here
  }
};
